import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserCircle2, ChevronRight, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";

/**
 * Feb 2026 · Concierge-Picker — Landung nach Klick auf „Concierge" im
 * Header-View-Switcher. Der Admin wählt hier den konkreten Concierge; die
 * Wahl (`impersonatedConcierge = { id, name }`) lebt im React-State
 * und filtert danach systemweit die Daten (Anfragen `assigned_to`,
 * Kalender `assignee_id`).
 *
 * Für echte Concierges ist diese Seite nicht relevant — sie werden
 * automatisch auf ihre eigene id gefiltert (siehe `activeConciergeId` in
 * `lib/auth.js`) und landen direkt in `/team`.
 */
export default function TeamConciergeSelect() {
  const nav = useNavigate();
  const { user, setImpersonatedConcierge, impersonatedConcierge, setEffectiveRole } = useAuth();
  const [concierges, setConcierges] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Feb 2026 · Directive „Admin-eigene Concierge-Ebene": Admin taucht
        // in der Picker-Liste mit dem eigenen Namen auf. Wählt er sich selbst,
        // sieht er die persönliche Concierge-Perspektive (nur eigene zugewiesene
        // Objekte + Tasks) — voll editierbar, weil `auth.js::isImpersonatingConcierge`
        // Self-Impersonation exkludiert. Andere Admins/Concierges bleiben
        // read-only (bekannte 403-Regel).
        //
        // Quelle bleibt die „Zugänge"-Liste (`/api/admin/allowed-emails`) —
        // damit erscheinen auch eingeladene Concierges ohne Login. Die
        // tatsächliche `user_id` mergen wir über `/team/members` (das nur
        // eingeloggte Nutzer + garantierte `id` liefert).
        const [allowed, members] = await Promise.all([
          api.get("/admin/allowed-emails"),
          api.get("/team/members").catch(() => ({ data: [] })),
        ]);
        const byEmail = new Map();
        for (const m of members.data || []) {
          if (m.email) byEmail.set(m.email.toLowerCase(), m.id);
        }
        // 1) Alle Concierges (inkl. eingeladen) aus allowed-emails.
        const conciergeRows = (allowed.data || [])
          .filter((r) => r.role === "concierge")
          .map((r) => ({
            ...r,
            resolved_user_id: byEmail.get((r.email || "").toLowerCase()) || r.user_id || null,
          }));
        // 2) Zusätzlich alle Admins aus /team/members — nur eingeloggte, weil
        //    ein Admin ohne Login nicht auf sein eigenes Cockpit zugreifen
        //    kann. Dedupe by user_id (nicht by email, damit z. B. ein Owner
        //    mit mehreren Rollen exakt einmal auftaucht).
        const admins = (members.data || [])
          .filter((m) => m.role === "admin")
          .map((m) => ({
            id: `admin-${m.id}`,
            email: m.email,
            name: m.name || m.email,
            role: "admin",
            resolved_user_id: m.id,
          }));
        const merged = [...admins, ...conciergeRows]
          .sort((a, b) => {
            // Admins immer oben; darunter alphabetisch.
            if (a.role !== b.role) return a.role === "admin" ? -1 : 1;
            return (a.name || a.email || "").localeCompare(b.name || b.email || "");
          });
        setConcierges(merged);
      } catch (e) {
        setConcierges([]);
      }
    })();
  }, []);

  const pick = (c) => {
    // Ohne resolved_user_id kein Filter möglich — Zeile ist disabled im UI.
    if (!c.resolved_user_id) return;
    // Wichtig: effectiveRole auf "concierge" setzen — sonst bleibt
    // `activeConciergeId=null` (siehe Ableitung in lib/auth.js), weil die
    // Impersonation ohne Perspektiv-Switch nicht greift. Ein Admin, der
    // direkt per URL zu /team/concierge-select navigiert, muss durch
    // die Auswahl-Aktion auch in die Concierge-Perspektive rutschen.
    setEffectiveRole("concierge");
    setImpersonatedConcierge({ id: c.resolved_user_id, name: c.name || c.email });
    nav("/team", { replace: true });
  };

  const clearAndBackToAdmin = () => {
    setImpersonatedConcierge(null);
    nav("/team", { replace: true });
  };

  // Nur Admins landen hier — echter Concierge wird in Layout.switchView
  // automatisch auf seine eigene id gesetzt und direkt zu /team geroutet.
  if (user && user.role !== "admin") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="concierge-select-forbidden">
        <p className="text-navy-dark/70">
          Nur Admins können die Concierge-Perspektive wechseln. Öffne diese Seite als Admin.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="concierge-select-page">
      <div className="mb-10">
        <div className="label-caps mb-3" style={{ color: BRAND_COLORS.gold }}>Weissblom · Team</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">Concierge auswählen</h1>
        <p className="text-[14px] text-navy-dark/70 mt-3 max-w-2xl leading-relaxed">
          Wähle den Concierge, dessen Perspektive du einnehmen willst. Du siehst
          danach systemweit nur die Anfragen und Kalender-Termine, die diesem
          Concierge zugeteilt sind. Zurückwechseln zu Admin über den Umschalter
          oben rechts.
        </p>
      </div>

      {concierges === null ? (
        <div className="flex items-center gap-2 text-[13px] text-navy-dark/50 italic">
          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.6} />
          Lade Concierge-Team …
        </div>
      ) : concierges.length === 0 ? (
        <div className="p-8 border border-dashed border-navy/20 text-[13px] text-navy-dark/60 text-center" data-testid="concierge-select-empty">
          Noch kein Concierge im Team. Lege unter „Zugänge" eine E-Mail mit
          Rolle „Concierge" an — sobald der Concierge sich zum ersten Mal
          einloggt, erscheint er hier.
        </div>
      ) : (
        <div className="space-y-3">
          {concierges.map((c) => {
            const isCurrent = c.resolved_user_id && impersonatedConcierge?.id === c.resolved_user_id;
            const invited = !c.resolved_user_id;
            const isSelf = c.role === "admin" && c.resolved_user_id === user?.id;
            const isAdmin = c.role === "admin";
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => pick(c)}
                disabled={invited}
                data-testid={`concierge-select-row-${c.resolved_user_id || c.id}`}
                data-invited={invited ? "true" : "false"}
                data-role={c.role || "concierge"}
                data-self={isSelf ? "true" : "false"}
                aria-pressed={isCurrent}
                className={`w-full bg-white border p-5 flex items-center justify-between gap-4 text-left transition-colors group ${
                  invited
                    ? "border-navy/10 opacity-60 cursor-not-allowed"
                    : "border-navy/15 hover:border-gold"
                }`}
                style={isCurrent ? { borderColor: BRAND_COLORS.gold, background: withAlpha(BRAND_COLORS.gold, 0.08) } : undefined}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: withAlpha(BRAND_COLORS.navy, 0.08), color: BRAND_COLORS.navy }}
                  >
                    <UserCircle2 className="w-6 h-6" strokeWidth={1.4} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-[17px] text-navy leading-tight truncate">
                      {c.name || c.email}
                      {isSelf && (
                        <span
                          className="ml-2 label-caps px-2 py-0.5 text-[9.5px] align-middle"
                          style={{ background: withAlpha(BRAND_COLORS.gold, 0.2), color: BRAND_COLORS.goldDark }}
                        >
                          Sie
                        </span>
                      )}
                      {isAdmin && !isSelf && (
                        <span
                          className="ml-2 label-caps px-2 py-0.5 text-[9.5px] align-middle"
                          style={{ background: withAlpha(BRAND_COLORS.navy, 0.08), color: BRAND_COLORS.navy }}
                        >
                          Admin
                        </span>
                      )}
                    </div>
                    <div className="text-[12px] text-navy-dark/60 truncate flex items-center gap-2 flex-wrap">
                      <span>{c.email}</span>
                      {isSelf && (
                        <span className="label-caps" style={{ color: BRAND_COLORS.goldDark }}>· voll editierbar</span>
                      )}
                      {isCurrent && !isSelf && (
                        <span className="label-caps" style={{ color: BRAND_COLORS.goldDark }}>· aktuell aktiv</span>
                      )}
                      {invited && (
                        <span
                          className="label-caps px-2 py-0.5 rounded-full text-[10px]"
                          style={{ background: withAlpha(BRAND_COLORS.gold, 0.15), color: BRAND_COLORS.goldDark }}
                          title="Dieser Concierge wurde eingeladen, hat sich aber noch nicht zum ersten Mal eingeloggt. Sobald er das tut, wird er hier wählbar."
                        >
                          Eingeladen · noch kein Login
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {!invited && (
                  <ChevronRight className="w-4 h-4 text-navy-dark/40 group-hover:text-gold" strokeWidth={1.6} />
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-8 pt-6 border-t border-navy/10">
        <button
          type="button"
          onClick={clearAndBackToAdmin}
          data-testid="concierge-select-back-admin"
          className="text-[11.5px] tracking-[0.2em] uppercase text-navy-dark/70 hover:text-navy transition-colors underline decoration-dotted decoration-navy-dark/40 underline-offset-4"
        >
          ← Doch als Admin weitermachen
        </button>
      </div>
    </div>
  );
}
